import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { CheckCircle2, ChevronLeft, ChevronRight, Circle, Loader2, MapPin, MessageCircle, Navigation, Phone, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { isIpad, waUrl } from '../../../lib/waShare';
import { formatErr } from '../../../components/admin/billing/shared';

const iso = (d) => d.toLocaleDateString('en-CA');
const shift = (s, n) => { const d = new Date(`${s}T00:00:00`); d.setDate(d.getDate() + n); return iso(d); };
const appleDevice = () => isIpad() || /iPhone|iPod/.test(navigator.userAgent);
// Apple Maps on iPhone/iPad, Google Maps everywhere else.
const mapsUrl = (addr) => (appleDevice()
  ? `https://maps.apple.com/?daddr=${encodeURIComponent(addr)}`
  : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`);
const car = (v) => [v?.make, v?.model, v?.registration_number].filter(Boolean).join(' · ');

const Stop = ({ r, i, onToggle }) => {
  const p = r.pickup || {};
  const done = !!p.picked_up_at;
  return (
    <li className={`rounded-2xl border p-4 ${done ? 'border-emerald-200 bg-emerald-50/60' : 'border-slate-200 bg-white'}`} data-testid={`pickup-stop-${r.id}`}>
      <div className="flex items-start gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-bold text-white">{i + 1}</span>
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-slate-900">{p.time || 'Time TBC'} · {r.customer?.name}</p>
          <p className="text-xs text-slate-500">{[car(r.vehicle), r.service_type, `Slot ${r.slot_window}`].filter(Boolean).join(' · ')}</p>
          <p className="mt-2 flex items-start gap-1.5 text-sm text-slate-800" data-testid={`pickup-address-${r.id}`}><MapPin size={15} className="mt-0.5 shrink-0 text-rose-600" />{p.address || 'No address given — call the customer'}</p>
          <p className="mt-1 text-xs text-slate-500">Driver: <b className="text-slate-700">{p.staff_name || 'Not assigned'}</b> · <Link to={`/admin/appointments/${r.id}`} className="text-teal-700 underline">{r.ref_no}</Link></p>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {p.address ? (
          <a href={mapsUrl(p.address)} target="_blank" rel="noopener noreferrer" data-testid={`pickup-directions-${r.id}`}
            className="col-span-1 flex items-center justify-center gap-1.5 rounded-xl bg-sky-600 py-3 text-sm font-semibold" style={{ color: '#fff' }}>
            <Navigation size={16} /> Directions
          </a>
        ) : <span className="flex items-center justify-center rounded-xl bg-slate-100 py-3 text-xs text-slate-400">No address</span>}
        <a href={`tel:${r.customer?.phone}`} data-testid={`pickup-call-${r.id}`}
          className="flex items-center justify-center gap-1.5 rounded-xl border border-slate-300 bg-white py-3 text-sm font-semibold text-slate-800">
          <Phone size={16} /> Call
        </a>
        <button type="button" onClick={() => onToggle(r, !done)} data-testid={`pickup-done-${r.id}`}
          className={`flex items-center justify-center gap-1.5 rounded-xl py-3 text-sm font-semibold ${done ? 'bg-emerald-600 text-white' : 'border border-emerald-300 bg-white text-emerald-800'}`}>
          {done ? <CheckCircle2 size={16} /> : <Circle size={16} />} {done ? 'Picked up' : 'Mark picked'}
        </button>
      </div>
    </li>
  );
};

export default function PickupsPage() {
  const { authHeader } = useAuth();
  const [date, setDate] = useState(iso(new Date()));
  const [mine, setMine] = useState(false);
  const [rows, setRows] = useState(null);
  const load = useCallback(() => {
    setRows(null);
    axios.get(`${API}/appointments/pickups`, { headers: authHeader(), params: { date, mine } })
      .then(({ data }) => setRows(data.rows)).catch((e) => { toast.error(formatErr(e)); setRows([]); });
  }, [authHeader, date, mine]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (r, done) => {
    setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, pickup: { ...x.pickup, picked_up_at: done ? new Date().toISOString() : undefined } } : x)));
    try { await axios.post(`${API}/appointments/${r.id}/picked-up`, {}, { headers: authHeader(), params: { done } }); } catch (e) { toast.error(formatErr(e)); load(); }
  };

  const nice = new Date(`${date}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' });
  const shareText = rows && [`*Pickups · ${nice}*`, ...rows.map((r, i) => `${i + 1}. ${r.pickup?.time || 'TBC'} · ${r.customer?.name} (${r.customer?.phone})\n   ${car(r.vehicle)}\n   ${r.pickup?.address || 'Address TBC'}${r.pickup?.address ? `\n   ${mapsUrl(r.pickup.address)}` : ''}\n   Driver: ${r.pickup?.staff_name || 'Not assigned'}`)].join('\n');
  const done = (rows || []).filter((r) => r.pickup?.picked_up_at).length;

  return (
    <div className="mx-auto max-w-3xl space-y-4 px-4 py-4 sm:px-0" data-testid="pickups-page">
      <div className="flex flex-wrap items-center gap-3">
        <Truck size={22} className="text-teal-700" />
        <h1 className="text-2xl font-semibold text-slate-900">Pickups</h1>
        {rows && <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700" data-testid="pickups-count">{done}/{rows.length} picked up</span>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setDate(shift(date, -1))} className="rounded-lg border border-slate-300 bg-white p-2" aria-label="Previous day" data-testid="pickups-prev"><ChevronLeft size={16} /></button>
        <button type="button" onClick={() => setDate(iso(new Date()))} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold" data-testid="pickups-today">Today</button>
        <button type="button" onClick={() => setDate(shift(date, 1))} className="rounded-lg border border-slate-300 bg-white p-2" aria-label="Next day" data-testid="pickups-next"><ChevronRight size={16} /></button>
        <span className="text-sm font-semibold text-slate-800" data-testid="pickups-date">{nice}</span>
        <label className="ml-auto flex items-center gap-1.5 text-xs font-semibold text-slate-700">
          <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} data-testid="pickups-mine" /> Mine only
        </label>
        {rows?.length > 0 && (
          <a href={waUrl('', shareText)} target="_blank" rel="noopener noreferrer" data-testid="pickups-share"
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-2 text-xs font-semibold" style={{ color: '#fff' }}>
            <MessageCircle size={13} /> Share list on WhatsApp
          </a>
        )}
      </div>
      {rows === null && <Loader2 className="animate-spin text-slate-400" />}
      {rows?.length === 0 && <p className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="pickups-empty">No pickups {mine ? 'assigned to you ' : ''}on this day.</p>}
      <ol className="space-y-3">{(rows || []).map((r, i) => <Stop key={r.id} r={r} i={i} onToggle={toggle} />)}</ol>
    </div>
  );
}
